import { SafeAreaView, StyleSheet } from 'react-native'
import { TestConfig, TestState, useStore } from 'zens-store'
import { Button, Color, Spacing, Text } from 'zens-storybook'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { useNavigation } from 'zens-router'

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginTop: Spacing.L,
    marginHorizontal: Spacing.M,
  },
  title: {
    textAlign: 'center',
  },
  stop: {
    marginTop: Spacing.L,
  },
  light: {
    color: Color.simplyWhite,
  },
})

interface Props {
  config?: TestConfig
  onCompleted?: () => void
  onFailed?: () => void
}
const TestStart = (props: Props) => {
  const { config, onCompleted, onFailed } = props
  const { test } = useStore()
  const { t } = useTranslation()
  const navigation = useNavigation()

  useEffect(() => {
    // Start test on mount
    if (!test.isRunning) {
      test.start(config)
    }

    // Restart test when navigating back to this screen
    const unsubscribe = navigation.addListener('focus', () => {
      // Only restart if we're coming back (not initial mount)
      if (test.isFailed || test.isCompleted) {
        restart()
      }
    })
    return unsubscribe
  }, [navigation])

  useEffect(() => {
    if (test.isFailed) {
      onFailed?.()
    } else if (test.isCompleted) {
      onCompleted?.()
    }
  }, [test.isCompleted, test.isFailed])

  function restart() {
    test.reset()
    test.start(config)
  }

  const titleForState = () => {
    switch (test.state) {
      case TestState.READY:
        return t('Place your phone on the charger to start the test')
      case TestState.STARTING:
        return t('Starting test')
      case TestState.WAITING:
        if (test.isCharging) return t('Remove phone from charger before starting the test')
        else return t('Place your phone on the charger')
      case TestState.SCANNING:
        return t('Looking for chargers')
      case TestState.RANGING:
        return t('Looking for beacons')
      case TestState.READING:
        return t('Reading charger data')
    }
  }

  return (
    <SafeAreaView style={styles.container}>
      <Text style={[styles.title, !test.isRunning && !test.isReady && styles.light]}>{titleForState()}</Text>
      {test.isRunning && <Button style={styles.stop} title={t('Stop') as string} onPress={restart} />}
    </SafeAreaView>
  )
}

export default observer(TestStart)
